import React from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { GlossaryTerm } from './journeyGlossary';

interface Props {
  term: GlossaryTerm | null;
  onClose: () => void;
}

export const GlossaryTermPanel: React.FC<Props> = ({ term, onClose }) => {
  if (!term) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1800]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
          <h2 className="text-base font-semibold text-gray-900">{term.title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Cerrar"
          >
            <X size={20} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <ol className="list-decimal ml-4 space-y-4 text-sm text-gray-700 leading-relaxed">
            {term.steps.map((step, i) => (
              <li key={i}>
                {step.text}
                {step.image && (
                  <img
                    src={step.image}
                    alt={typeof step.text === 'string' ? step.text : term.title}
                    className="mt-2 w-full rounded-lg border border-gray-200 shadow-sm"
                  />
                )}
              </li>
            ))}
          </ol>
          {term.table && (
            <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    {term.table.headers.map((h, i) => (
                      <TableHead key={i} className="text-xs font-semibold text-gray-700">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {term.table.rows.map((row, i) => (
                    <TableRow key={i}>
                      {row.map((cell, j) => (
                        <TableCell key={j} className="text-xs text-gray-600 align-top">{cell}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {term.note && (
            <div className="mt-4 text-xs text-gray-400 border-t border-gray-100 pt-3 whitespace-pre-line">{term.note}</div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};
